<template>
  <div class="panel" v-if="visibleRows.length">
    <div class="cm-sec-head">
      <h2>{{ title }}</h2>
      <p class="cm-hint">{{ hint }}</p>
      <slot name="header-controls"></slot>
    </div>

    <table class="cm-table">
      <thead>
        <tr>
          <th
            :class="classTableHead('main', 'term')"
            @click="toggleSort('main', 'term')"
          >{{ $t('CM_COL_TERM') }}</th>
          <th
            :class="['is-num', classTableHead('main', 'posts')]"
            @click="toggleSort('main', 'posts')"
          >{{ $t('CM_COL_POSTS') }}</th>
          <th
            v-if="showEr"
            :class="classTableHead('main', 'er')"
            @click="toggleSort('main', 'er')"
          >{{ $t('CM_COL_REL_ER') }}</th>
          <th
            v-if="showTr"
            :class="classTableHead('main', 'tr')"
            @click="toggleSort('main', 'tr')"
          >{{ $t('CM_COL_REL_TR') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in visibleRows"
          :key="row.term"
          :class="row.added ? 'is-tracked' : null"
        >
          <td>
            <span class="cm-term" @click="openTerm(row.term)">
              <template v-if="row.added">★ </template>{{ row.label || row.term }}
            </span>
          </td>
          <td class="is-num">{{ formatNum(row.posts_count) }}</td>
          <td v-if="showEr">
            <span class="cm-bar">
              <span class="cm-bar-track">
                <span
                  class="cm-bar-fill"
                  :class="`cm-tone-${tone}`"
                  :style="{ width: barWidth(row.avg_relative_er, 'er') }"
                ></span>
              </span>
              <span class="cm-bar-val">{{ formatPct(row.avg_relative_er) }}</span>
            </span>
          </td>
          <td v-if="showTr">
            <span class="cm-bar">
              <span class="cm-bar-track">
                <span
                  class="cm-bar-fill is-alt"
                  :class="`cm-tone-${tone}`"
                  :style="{ width: barWidth(row.avg_relative_tr, 'tr') }"
                ></span>
              </span>
              <span class="cm-bar-val">{{ formatPct(row.avg_relative_tr) }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <button v-if="ranked.length > limit" class="cm-more" @click="showAll = !showAll">
      {{ showAll ? $t('CM_SHOW_LESS') : $t('CM_SHOW_ALL', { n: ranked.length }) }}
    </button>
  </div>
</template>

<script>
import bus from "@/models/content-monitoring/bus";
import pageUtils from "@/models/page-utils";
import cmTableSortMixin, { createSortState } from "@/mixins/cmTableSortMixin";

export default {
  name: "Cm-top-table",
  mixins: [cmTableSortMixin],

  props: {
    title: String,
    hint: String,
    // ранжированные строки топа
    rows: { type: Array, default: () => [] },
    // ★-слова: закрепляются над топом, даже если не попали в него
    tracked: { type: Array, default: () => [] },
    // какие метрики показывать: both | er | tr
    variant: { type: String, default: "both" },
    // цветовая семья баров: indigo | violet | teal | orange | green | pink | amber
    tone: { type: String, default: "indigo" },
  },

  data() {
    return {
      showAll: false,
      limit: 10,
      sorts: {
        main: createSortState(["term", "posts", "er", "tr"], "term"),
      },
    };
  },

  computed: {
    showEr() {
      return this.variant === "both" || this.variant === "er";
    },
    showTr() {
      return this.variant === "both" || this.variant === "tr";
    },

    /** ★-слова идут первыми и не дублируются в ранжированной части. */
    pinned() {
      return this.tracked.map((t) => ({ ...t, added: true }));
    },
    ranked() {
      const pinnedTerms = new Set(this.pinned.map((t) => t.term));
      return this.rows.filter((r) => !pinnedTerms.has(r.term));
    },
    /**
     * В нейтральном состоянии ★-слова закреплены сверху, затем идёт топ ranked.
     * При активной сортировке ★-слова участвуют в сортировке наравне со всеми,
     * а сортировка применяется ко всему списку до обрезки по limit —
     * поэтому скрытые строки могут попасть в видимый топ.
     */
    visibleRows() {
      const sortOpts = {
        fieldMap: {
          term: "term",
          posts: "posts_count",
          er: "avg_relative_er",
          tr: "avg_relative_tr",
        },
        stringFields: ["term"],
        stringValue: (row) => (row.label || row.term || "").toString(),
      };

      if (this.sorts.main[this.sorts.main.type] === 0) {
        const tail = this.showAll ? this.ranked : this.ranked.slice(0, this.limit);
        return [...this.pinned, ...tail];
      }
      const all = this.sortRows([...this.pinned, ...this.ranked], "main", sortOpts);
      return this.showAll ? all : all.slice(0, this.limit);
    },

    maxEr() {
      return Math.max(...this.visibleRows.map((r) => r.avg_relative_er), 1);
    },
    maxTr() {
      return Math.max(...this.visibleRows.map((r) => r.avg_relative_tr), 1);
    },
  },

  methods: {
    formatNum(num) {
      return pageUtils.formatNumbers(num);
    },
    /**
     * null = метрики нет, и это не ноль: относит. обсуждаемость не считается
     * у страниц без комментарийного фона (порог — в top_discussion.sql).
     */
    formatPct(value) {
      return value == null ? "—" : `${value}%`;
    },
    barWidth(value, kind) {
      if (value == null) return "0%";
      const max = kind === "er" ? this.maxEr : this.maxTr;
      return `${Math.max(2, Math.round((value / max) * 100))}%`;
    },
    openTerm(term) {
      bus.$emit("cm:term", term);
    },
  },
};
</script>
